import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICES = ["Managed IT Services", "Custom Software", "Both / Not sure"];

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", service: SERVICES[0], message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); if (error) setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please fill in your name, email and a short message.");
      return;
    }
    setError("");
    setStatus("sending");
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setError("Something went wrong. Please email us directly at contact@quadrimtech.com");
    }
  };

  return (
    <div
      data-testid="contact-form"
      className="rounded-2xl border border-white/10 bg-surface p-6 md:p-8"
    >
      <AnimatePresence mode="wait">
        {status === "done" ? (
          <motion.div
            key="success"
            data-testid="contact-form-success"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="py-8 text-center"
          >
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-accent text-black mb-6">
              <Check className="h-8 w-8" />
            </div>
            <h3 className="font-heading text-3xl font-medium tracking-tight mb-3">Thank you!</h3>
            <p className="text-[#A1A1AA] max-w-sm mx-auto">
              Thank you for your enquiry — one of our engineers will get back to
              you within the next 2 days.
            </p>
            <button
              data-testid="contact-form-reset"
              onClick={() => {
                setStatus("idle");
                setForm({ name: "", email: "", company: "", service: SERVICES[0], message: "" });
              }}
              className="mt-8 rounded-full border border-white/15 px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] hover:border-accent transition-colors duration-300"
            >
              Send another
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onSubmit={submit}
            noValidate
            className="space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name *">
                <input data-testid="contact-name-input" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="lead-input" />
              </Field>
              <Field label="Email *">
                <input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" className="lead-input" />
              </Field>
            </div>

            <Field label="Company">
              <input data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Company name" className="lead-input" />
            </Field>

            <div>
              <span className="block font-mono text-[11px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-2">What do you need?</span>
              <div className="flex flex-wrap gap-2">
                {SERVICES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`contact-service-${s.split(" ")[0].toLowerCase()}`}
                    onClick={() => { setForm((f) => ({ ...f, service: s })); if (error) setError(""); }}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${
                      form.service === s ? "border-accent bg-accent text-black" : "border-white/15 text-[#A1A1AA] hover:border-white/40"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <Field label="Message *">
              <textarea data-testid="contact-message-input" value={form.message} onChange={set("message")} rows={5} placeholder="A sentence or two about your project..." className="lead-input resize-none" />
            </Field>

            {error && (
              <p data-testid="contact-form-error" className="text-sm text-red-400">{error}</p>
            )}

            <button
              data-testid="contact-form-submit"
              type="submit"
              disabled={status === "sending"}
              className="group w-full flex items-center justify-center gap-3 rounded-full bg-white text-black px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] hover:bg-accent transition-colors duration-300 disabled:opacity-60"
            >
              {status === "sending" ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Sending</>
              ) : (
                <>Send enquiry <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" /></>
              )}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[11px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-2">{label}</span>
      {children}
    </label>
  );
}
